import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { STUFE_INFO, STUFEN, type Stufe } from '../../../shared/akquise';
import { aktion, fmt, useDaten } from '../../daten';
import { Symbol, useHinweis, vars } from '../../komponenten/Grundbausteine';
import { gehe, suchwert } from '../../ort';
import { FirmaFenster } from './FirmaFenster';
import type { Baum, Kampagne, Tabelle, Zeile } from './typen';

const FILTER: [string, string][] = [['alle', 'Alle'], ['anr', 'In Anreicherung'], ['pruef', 'Prüfen'], ['frei', 'Fertig und frei'], ['kamp', 'In Kampagne'], ['erl', 'Erledigt'], ['ohneweb', 'Ohne Website']];
const SPALTEN: [string, string][] = [['stage', 'Stufe'], ['weg', 'Kontaktweg'], ['foerder', 'Förderung'], ['web', 'Website'], ['quelle', 'Quelle'], ['ma', 'Mitarbeiter'], ['mailst', 'E-Mail-Status'], ['camp', 'Kampagne']];
const SPALTEN_SPEICHER = 'nt-spalten';
// Reihenfolge der Anreicherung (auswahl.ts): jede Stufe nimmt je Lauf die Firmen mit der kleinsten Firmen-ID zuerst, über alle Listen
const REIHENFOLGE = 'Ein Schalter für alle Listen. Alle 10 Minuten nimmt jede Stufe (Kategorie, Impressum, Förderquote, E-Mail) die noch offenen Firmen in der Reihenfolge der Firmen-ID, über alle Listen und Bundesländer hinweg; neue Firmen kommen hinten dran. Eine Firma geht zur nächsten Stufe, sobald die vorige fertig ist. Angehaltene Listen (Ausnahme) werden übersprungen.';

function spaltenLesen(): Set<string> {
  try { const s = JSON.parse(localStorage.getItem(SPALTEN_SPEICHER) ?? 'null'); if (Array.isArray(s)) return new Set(s); } catch { /* Standard */ }
  return new Set(['stage', 'weg', 'foerder']);
}

/* ---------- Zellen wie im Prototyp (cell()) ---------- */
function Zelle({ f, k }: { f: Zeile; k: string }) {
  if (k === 'stage') {
    const info = STUFE_INFO[f.stufe as Stufe];
    const sub = f.stufe === 'anr' ? STUFEN[f.schritt - 1] : f.stufe === 'pruef' ? `${f.pruefung} · ${f.pruefung === 'Kategorie und Förderung' ? 'bremst nicht' : 'hält an'}`
      : f.stufe === 'kamp' ? `${f.angebot}: ${f.angebotStatus}` : f.stufe === 'ende' ? f.ende : f.stufe === 'crm' ? 'Termin gebucht' : '';
    return <><span className={`state ${info.ton}`}>{info.label}</span>{sub && <span className="sub">{sub}</span>}</>;
  }
  if (k === 'weg') return <>{f.weg ?? <span className="meta">noch keiner</span>}<span className="sub">{f.ap ? `${f.ap}, ${f.position ?? 'Ansprechpartner'}` : 'kein Ansprechpartner'}</span></>;
  if (k === 'foerder') return f.foerder === 'ja' ? <>{f.quoteBestimmt && f.quote ? `ja · ${f.quote} %` : 'ja · Quote offen'}</> : f.foerder === 'nein' ? <span className="meta">nein</span> : f.foerder === 'prüfen' ? <span className="state warn">prüfen</span> : <span className="meta">offen</span>;
  if (k === 'web') { const u = /^(nicht anbieten|anbieten|unsicher)/.exec(f.bewertung)?.[1]; return u ? <>{u}</> : f.bewertung ? <span className="meta">{f.bewertung.split(' - ')[0]}</span> : <span className="meta">offen</span>; }
  if (k === 'quelle') return <>{f.quelle ?? <span className="meta">offen</span>}{f.weitere && f.weitere !== f.quelle && <span className="sub">auch {f.weitere}</span>}</>;
  if (k === 'ma') return <>{f.ma ?? <span className="meta">offen</span>}</>;
  if (k === 'mailst') return f.mailStatus ? <>{f.mailStatus}</> : <span className="meta">offen</span>;
  if (k === 'camp') return f.kampagne ? <span className="camp" title={f.kampagne}>{f.kampagne}</span> : <span className="meta">frei</span>;
  return null;
}

export function Listen({ listeId }: { listeId: string | undefined }) {
  const hinweis = useHinweis();
  const baum = useDaten<Baum>('/api/akquise/baum');
  const kampagnen = useDaten<Kampagne[]>('/api/akquise/kampagnen');
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [naviOffen, setNaviOffen] = useState(false);
  const [filter, setFilter] = useState('alle');
  const [q, setQ] = useState('');
  const [qVerz, setQVerz] = useState('');
  const [sort, setSort] = useState<{ key: string | null; dir: 1 | -1 }>({ key: null, dir: 1 });
  const [seite, setSeite] = useState(1);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [spalten, setSpalten] = useState<Set<string>>(spaltenLesen);
  const [spaltenMenue, setSpaltenMenue] = useState(false);
  const [firma, setFirma] = useState<string | null>(() => suchwert('firma'));
  const firmaAusAdresse = suchwert('firma');
  useEffect(() => { if (firmaAusAdresse) setFirma(firmaAusAdresse); }, [firmaAusAdresse]);
  const [blitz, setBlitz] = useState<Set<string>>(new Set());
  const [bulkKamp, setBulkKamp] = useState('');
  const suchFeld = useRef<HTMLInputElement>(null);

  // Gewählte Liste: aus der Adresse, sonst die erste im Baum
  const alle = useMemo(() => baum.daten?.laender.flatMap(l => l.kats.flatMap(k => k.listen.map(li => ({ ...li, land: l.land, kat: k.kat })))) ?? [], [baum.daten]);
  const aktuell = alle.find(l => String(l.id) === listeId) ?? alle[0];
  useEffect(() => {
    if (!aktuell) return;
    if (String(aktuell.id) !== listeId) gehe(`/akquise/listen/${aktuell.id}${location.search}`, true);
    setOffen(o => (o.has(aktuell.land) && o.has(`${aktuell.land}/${aktuell.kat}`) ? o : new Set([...o, aktuell.land, `${aktuell.land}/${aktuell.kat}`])));
  }, [aktuell, listeId]);

  // Liste gewechselt: Auswahl, Suche und Sortierung zurück (wie im Prototyp)
  const vorige = useRef<number | null>(null);
  const [wechsel, setWechsel] = useState(false);
  useEffect(() => {
    if (!aktuell || vorige.current === aktuell.id) return;
    if (vorige.current !== null) { setSel(new Set()); setQ(''); setQVerz(''); setSort({ key: null, dir: 1 }); setSeite(1); setWechsel(true); }
    vorige.current = aktuell.id;
  }, [aktuell]);

  useEffect(() => { const t = setTimeout(() => { setQVerz(q.trim()); setSeite(1); }, 200); return () => clearTimeout(t); }, [q]);
  useEffect(() => { try { localStorage.setItem(SPALTEN_SPEICHER, JSON.stringify([...spalten])); } catch { /* nur diese Sitzung */ } }, [spalten]);
  useEffect(() => {
    if (!spaltenMenue) return;
    const klick = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest('#colMenu, #colBtn')) setSpaltenMenue(false); };
    document.addEventListener('click', klick);
    return () => document.removeEventListener('click', klick);
  }, [spaltenMenue]);

  const abfrage = aktuell ? `/api/akquise/listen/${aktuell.id}?${new URLSearchParams({ filter, q: qVerz, seite: String(seite), ...(sort.key ? { sort: sort.key, ab: sort.dir === -1 ? '1' : '0' } : {}) })}` : null;
  const tabelle = useDaten<Tabelle>(abfrage);
  useEffect(() => { if (!tabelle.laedt) setWechsel(false); }, [tabelle.laedt]);
  const neu = useCallback(() => { tabelle.neu(); baum.neu(); }, [tabelle, baum]);

  // Beim Wechsel der Liste nie den Kopf der vorigen zeigen (Baum 849, Kopf „2 Firmen“)
  const kopf = tabelle.daten?.kopf.id === aktuell?.id ? tabelle.daten?.kopf : undefined;
  const zeilen = tabelle.daten?.zeilen ?? [];
  const aktiveKampagnen = kampagnen.daten ?? [];
  const gewaehlteKampagne = bulkKamp || String(aktiveKampagnen[0]?.id ?? '');

  const umschalten = (k: string) => setOffen(o => { const n = new Set(o); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const sortieren = (k: string) => { setSort(s => (s.key === k ? { key: k, dir: (s.dir * -1) as 1 | -1 } : { key: k, dir: 1 })); setSeite(1); };
  const filtern = (k: string) => { setFilter(k); setSeite(1); };
  const alleGewaehlt = zeilen.length > 0 && zeilen.every(f => sel.has(f.fid)), einigeGewaehlt = zeilen.some(f => sel.has(f.fid));
  const cbAlle = useRef<HTMLInputElement>(null);
  useEffect(() => { if (cbAlle.current) cbAlle.current.indeterminate = einigeGewaehlt && !alleGewaehlt; });

  const alleAn = baum.daten?.alle ?? true;
  const alleSchalten = async () => {
    try {
      await aktion('anreicherung_alle', { laeuft: !alleAn });
      hinweis(!alleAn ? 'Anreicherung für alle Listen gestartet.' : 'Anreicherung für alle Listen gestoppt. Firmen, die gerade in einer Stufe sind, werden noch fertig.');
      neu();
    } catch (e) { hinweis(`Geht nicht: ${(e as Error).message}`); }
  };
  const anreicherung = async () => {
    if (!kopf) return;
    try {
      await aktion('anreicherung', { liste: kopf.id, laeuft: !kopf.laeuft });
      hinweis(!kopf.laeuft ? `Anreicherung für „${kopf.name}“ gestartet.` : `Anreicherung für „${kopf.name}“ gestoppt. Firmen, die gerade in einer Stufe sind, werden noch fertig.`);
      neu();
    } catch (e) { hinweis(`Geht nicht: ${(e as Error).message}`); }
  };

  const zuordnen = async () => {
    const ids = [...sel], k = aktiveKampagnen.find(x => String(x.id) === gewaehlteKampagne);
    if (!k) return;
    try {
      const r = await aktion<{ zugeordnet: number; uebersprungen: Record<string, number> }>('zuordnen', { firmen: ids, kampagne: k.id });
      const skip = Object.entries(r.uebersprungen).map(([g, n]) => `${n} ${g}`).join(', ');
      hinweis(`${r.zugeordnet} ${r.zugeordnet === 1 ? 'Firma' : 'Firmen'} „${k.name}“ zugeordnet` + (skip ? `; übersprungen: ${skip}.` : '.'));
      setSel(new Set());
      setBlitz(new Set(ids));
      setTimeout(() => setBlitz(new Set()), 1300);
      neu();
    } catch (e) { hinweis(`Geht nicht: ${(e as Error).message}`); }
  };

  const filterIndex = FILTER.findIndex(([k]) => k === filter);
  const sichtbar = SPALTEN.filter(([k]) => spalten.has(k));
  const th = (key: string, label: string) => (
    <th key={key} className={key === 'name' || key === 'stage' ? '' : 't-hide-sm'} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button className="sort" data-sort={key} onClick={() => sortieren(key)}>{label}<Symbol id="sort" /></button>
    </th>
  );
  const ohneListen = baum.daten?.ohne ?? [];

  return (
    <div className="listen">
      <aside className={naviOffen ? 'panel navi open' : 'panel navi'} id="navi" aria-label="Listen wählen">
        <button className="btn navi-toggle" id="naviToggle" aria-expanded={naviOffen} onClick={() => setNaviOffen(!naviOffen)}><Symbol id="folder" klein /><span id="naviPath">{aktuell ? `${aktuell.land} › ${aktuell.kat} › ${aktuell.name}` : 'Liste wählen'}</span></button>
        <p className="meta navi-title" style={{ padding: '4px 8px' }}>Bundesland, Überkategorie, Liste</p>
        <ul className="tree" id="tree">
          {baum.daten?.laender.map(l => {
            // Zahl am Land = Firmen in allen Listen des Landes (wie an der Liste); Zahl der Listen im Tooltip
            // Zahl am Land = Anzahl Listen, ausgeschrieben, damit sie nicht als Firmenzahl gelesen wird (Leon 2026-10-29)
            const lo = offen.has(l.land), nL = l.kats.reduce((a, k) => a + k.listen.length, 0), nF = l.kats.reduce((a, k) => a + k.listen.reduce((b, li) => b + li.n, 0), 0);
            return (
              <li key={l.land}><button className="land" data-t={l.land} aria-expanded={lo} onClick={() => umschalten(l.land)}><svg className="i chev" aria-hidden="true"><use href="#i-chev" /></svg>{l.land}<span className="n" title={`${nL} ${nL === 1 ? 'Liste' : 'Listen'} mit ${fmt(nF)} Firmen`}>{nL} {nL === 1 ? 'Liste' : 'Listen'}</span></button>
                {lo && <ul>{l.kats.map(k => {
                  const key = `${l.land}/${k.kat}`, ko = offen.has(key);
                  return (
                    <li key={key}><button data-t={key} aria-expanded={ko} onClick={() => umschalten(key)}><svg className="i chev" aria-hidden="true"><use href="#i-chev" /></svg>{k.kat}</button>
                      {ko && <ul>{k.listen.map(li => (
                        <li key={li.id}><button className="leaf" data-l={li.id} aria-current={li.id === aktuell?.id} onClick={() => { if (li.id !== aktuell?.id) { gehe(`/akquise/listen/${li.id}`); setNaviOffen(false); } }}>{li.name}<span className="n">{fmt(li.n)}</span></button></li>
                      ))}</ul>}
                    </li>
                  );
                })}</ul>}
              </li>
            );
          })}
          {baum.daten && <li><button className="land" data-t="__leer" aria-expanded={offen.has('__leer')} style={{ color: 'var(--text-2)', fontWeight: 400 }} onClick={() => umschalten('__leer')}><svg className="i chev" aria-hidden="true"><use href="#i-chev" /></svg>Ohne Listen<span className="n">{ohneListen.length}</span></button>
            {offen.has('__leer') && <ul>{ohneListen.map(l => <li key={l}><button disabled>{l}</button></li>)}</ul>}</li>}
        </ul>
      </aside>

      <div style={{ minWidth: 0 }}>
        <header className="page-head" style={{ marginBottom: 16 }}>
          <div>
            <nav className="crumbs" id="crumbs" aria-label="Pfad"><span>Listen</span><Symbol id="chev" /><span>{kopf?.land ?? aktuell?.land}</span><Symbol id="chev" /><span>{kopf?.kat ?? aktuell?.kat}</span></nav>
            <h1 id="h-list">{kopf?.name ?? aktuell?.name ?? 'Liste'}</h1>
            <p className="meta" style={{ maxWidth: 720 }}>{REIHENFOLGE}</p>
            <p className="meta" id="listMeta">{kopf ? `${fmt(kopf.n)} Firmen · ${kopf.n ? Math.round(kopf.fertig / kopf.n * 100) : 0} % fertig angereichert · ${kopf.inPruefung} in Prüfung` : aktuell ? `${fmt(aktuell.n)} Firmen · lädt …` : baum.fehler || tabelle.fehler || ''}</p>
          </div>
          <div className="run-chip" id="runChip">{kopf && <>
            <span className={alleAn && kopf.laeuft ? 'state ok run' : 'state neutral'} title={REIHENFOLGE}>Anreicherung {!alleAn ? 'für alle Listen gestoppt' : kopf.laeuft ? 'läuft (alle Listen)' : 'für diese Liste angehalten'}</span>
            <button className="btn btn-sm" id="runAlle" onClick={alleSchalten}><Symbol id={alleAn ? 'pause' : 'play'} klein />{alleAn ? 'Alle Listen stoppen' : 'Alle Listen starten'}</button>
            {alleAn && <button className="btn btn-sm btn-ghost" id="runBtn" onClick={anreicherung}>{kopf.laeuft ? 'Nur diese Liste anhalten' : 'Diese Liste wieder mitnehmen'}</button>}
          </>}</div>
        </header>

        <div className="toolbar">
          <div className="seg-scroll"><div className="seg" id="filterSeg" style={vars({ '--n': FILTER.length, '--i': filterIndex })} role="group" aria-label="Nach Stufe filtern">
            <span className="seg-thumb" aria-hidden="true" />
            {FILTER.map(([k, l]) => <button key={k} data-f={k} aria-pressed={k === filter} onClick={() => filtern(k)}>{l}</button>)}
          </div></div>
          <div className="tools-right">
            <label className="field"><Symbol id="search" klein /><input ref={suchFeld} className="input" id="q" type="search" placeholder="Firma, Ort, Ansprechpartner" aria-label="In dieser Liste suchen" value={q} onChange={e => setQ(e.target.value)} /></label>
            <div className="col-wrap">
              <button className="btn" id="colBtn" aria-expanded={spaltenMenue} aria-haspopup="true" onClick={() => setSpaltenMenue(!spaltenMenue)}><Symbol id="sliders" klein />Spalten</button>
              <div className="col-menu" id="colMenu" hidden={!spaltenMenue}>
                {SPALTEN.map(([k, l]) => <label key={k}><input type="checkbox" data-col={k} checked={spalten.has(k)} onChange={e => setSpalten(s => { const n = new Set(s); if (e.target.checked) n.add(k); else n.delete(k); return n; })} />{l}</label>)}
              </div>
            </div>
          </div>
        </div>

        <div className="panel table-card">
          <div className="table-scroll" id="tableScroll">
            {wechsel || (!tabelle.daten && !tabelle.fehler) ? (
              <table className="t firms"><tbody>{Array.from({ length: 8 }, (_, i) => (
                <tr key={i}><td><span className="sk" style={{ width: 16 }} /></td><td><span className="sk" style={{ width: `${45 + (i * 13) % 30}%` }} /></td><td><span className="sk" style={{ width: '60%' }} /></td><td className="t-hide-sm"><span className="sk" style={{ width: 96 }} /></td><td className="t-hide-sm"><span className="sk" style={{ width: '70%' }} /></td></tr>
              ))}</tbody></table>
            ) : tabelle.fehler ? (
              <div className="empty"><span className="empty-icon"><Symbol id="alert" /></span><h2>Liste nicht geladen</h2><p>{tabelle.fehler}</p><button className="btn" onClick={neu}>Noch einmal versuchen</button></div>
            ) : !zeilen.length ? (
              <div className="empty"><span className="empty-icon"><Symbol id="search" /></span><h2>Keine Firma passt</h2>
                <p>{qVerz ? `Zu „${qVerz}“ gibt es in dieser Ansicht keinen Treffer.` : 'In dieser Stufe ist gerade keine Firma.'}</p>
                <button className="btn" id="resetQ" onClick={() => { setQ(''); setQVerz(''); filtern('alle'); }}>Filter zurücksetzen</button></div>
            ) : (
              <table className="t firms"><thead><tr>
                <th><input ref={cbAlle} type="checkbox" className="cb" id="cbAll" aria-label="Alle auf dieser Seite auswählen" checked={alleGewaehlt}
                  onChange={e => setSel(s => { const n = new Set(s); zeilen.forEach(f => (e.target.checked ? n.add(f.fid) : n.delete(f.fid))); return n; })} /></th>
                {th('name', 'Firma')}{sichtbar.map(([k, l]) => th(k, l))}
              </tr></thead><tbody>
                {zeilen.map(f => (
                  <tr key={f.fid} tabIndex={0} data-id={f.fid} className={[sel.has(f.fid) ? 'sel' : '', blitz.has(f.fid) ? 'flash' : ''].filter(Boolean).join(' ') || undefined}
                    onClick={e => { if ((e.target as HTMLElement).closest('td:first-child')) return; setFirma(f.fid); }}
                    onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setFirma(f.fid); } }}>
                    <td><input type="checkbox" className="cb" aria-label={`${f.name} auswählen`} checked={sel.has(f.fid)} onChange={e => setSel(s => { const n = new Set(s); if (e.target.checked) n.add(f.fid); else n.delete(f.fid); return n; })} /></td>
                    <td className="cell-firm"><span className="name" title={f.name}>{f.name}</span><span className="sub"><span className="num">{f.fid}</span> · {f.plz} {f.ort}{f.ohneWebsite ? ' · ohne Website' : ''}</span></td>
                    {sichtbar.map(([k]) => <td key={k} className={k === 'stage' ? '' : 't-hide-sm'}><Zelle f={f} k={k} /></td>)}
                  </tr>
                ))}
              </tbody></table>
            )}
          </div>
          <div className="table-foot" id="tableFoot">
            {wechsel || !tabelle.daten ? <span className="meta">Lädt …</span> : !zeilen.length ? <span className="meta">0 von {fmt(kopf?.n ?? 0)} Firmen</span> : <>
              <span className="meta num">{fmt((seite - 1) * tabelle.daten.proSeite + 1)}–{fmt((seite - 1) * tabelle.daten.proSeite + zeilen.length)} von {fmt(tabelle.daten.gesamt)} Firmen{tabelle.daten.gesamt !== kopf?.n ? ` (gefiltert aus ${fmt(kopf?.n ?? 0)})` : ''}</span>
              <div className="pager">
                <button className="btn btn-sm" disabled={seite <= 1} onClick={() => setSeite(seite - 1)}>Zurück</button>
                <button className="btn btn-sm" disabled={seite * tabelle.daten.proSeite >= tabelle.daten.gesamt} onClick={() => setSeite(seite + 1)}>Weiter</button>
              </div>
            </>}
          </div>
        </div>

        <div className={sel.size ? 'bulk show' : 'bulk'} id="bulk" role="region" aria-label="Auswahl">
          <span><strong id="bulkN">{sel.size}</strong> ausgewählt</span>
          <select className="input" id="bulkCamp" aria-label="Kampagne wählen" value={gewaehlteKampagne} onChange={e => setBulkKamp(e.target.value)}>
            {aktiveKampagnen.map(k => <option key={k.id} value={k.id}>{k.name}{k.zustand === 'Entwurf' ? ' (Entwurf)' : ''}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" id="bulkGo" onClick={zuordnen} disabled={!aktiveKampagnen.length}>In Kampagne zuordnen</button>
          <button className="btn btn-ghost btn-sm" id="bulkClear" onClick={() => setSel(new Set())}>Auswahl aufheben</button>
        </div>
      </div>

      <FirmaFenster fid={firma} schliessen={() => { setFirma(null); if (location.search) gehe(location.pathname, true); }} geaendert={neu} />
    </div>
  );
}

